<template>
    <div class="tax-batch-result sub-page" v-loading="loading">
        <el-breadcrumb separator="/">
            <i class="el-icon-location"></i>
            <el-breadcrumb-item>计算个税</el-breadcrumb-item>
            <el-breadcrumb-item>
                <router-link to="/index/tax/batch">结算试算</router-link>
            </el-breadcrumb-item>
            <el-breadcrumb-item>试算结果</el-breadcrumb-item>
        </el-breadcrumb>

        <div v-if="summary" class="content-box">
            <div class="settlement">
                <div class="money">
                    <span>共计</span>
                    <span class="text-primary">{{ summary.total }} 笔</span>
                    <span>，金额</span>
                    <span class="text-primary">¥{{ formatAmount(summary.total_money) }}</span>
                </div>
                <div class="actions">
                    <el-button
                        type="default"
                        size="small"
                        @click="handleExport">
                        导出结果
                    </el-button>
                    <el-button
                        type="primary"
                        size="small"
                        @click="backToUpload">
                        重新上传
                    </el-button>
                </div>
            </div>

            <div class="data-card__box">
                <div class="data-card">
                    <div class="content">
                        <i class="el-icon-caret-right"></i>
                        <span class="bd">试算成功（笔·元）</span>
                        <div class="hd">{{ summary.success }} 笔 · ¥{{ formatAmount(summary.success_money) }}</div>
                    </div>
                    <div class="icon">
                        <v-svg-icon class="svg-icon" name="icon-icon_xiaoyanchenggong" />
                    </div>
                </div>
                <div class="data-card">
                    <div class="content">
                        <i class="el-icon-caret-right"></i>
                        <span class="bd">试算失败（笔·元）</span>
                        <div class="hd">{{ summary.fail }} 笔 · ¥{{ formatAmount(summary.fail_money) }}</div>
                    </div>
                    <div class="icon">
                        <v-svg-icon class="svg-icon" name="icon-icon_xiaoyanshibai" />
                    </div>
                </div>
                <div class="data-card">
                    <div class="content">
                        <i class="el-icon-caret-right"></i>
                        <span class="bd">试算个税合计（元）</span>
                        <div class="hd">¥{{ formatAmount(summary.success_tax) }}</div>
                    </div>
                    <div class="icon">
                        <v-svg-icon class="svg-icon" name="icon-icon_fuwufei" />
                    </div>
                </div>
                <div class="data-card">
                    <div class="content">
                        <i class="el-icon-caret-right"></i>
                        <span class="bd">试算总支付金额（元）</span>
                        <div class="hd">¥{{ formatAmount(summary.to_pay) }}</div>
                    </div>
                    <div class="icon">
                        <v-svg-icon class="svg-icon" name="icon-icon_daizheng" />
                    </div>
                </div>
            </div>

            <div class="table">
                <el-table
                    :data="pagedTableData"
                    border
                    stripe
                    size="small"
                    height="480"
                    :row-class-name="rowClassName">
                    <el-table-column
                        type="index"
                        label="序号"
                        width="60"
                        align="center"
                        :index="indexMethod" />
                    <el-table-column prop="name" label="姓名" min-width="100" />
                    <el-table-column prop="idcard" label="身份证号" min-width="160" show-overflow-tooltip />
                    <el-table-column prop="money" label="服务金额" min-width="120" />
                    <el-table-column prop="service_fee" label="服务费" min-width="120" />
                    <el-table-column prop="to_pay" label="应支付金额" min-width="140" />
                    <el-table-column prop="diff_money" label="大额服务费" min-width="120" />
                    <el-table-column prop="tax" label="个税" min-width="120" />
                    <el-table-column prop="real_money" label="实收金额" min-width="140" />
                    <el-table-column label="异常原因" min-width="220" align="center">
                        <template slot-scope="scope">
                            <span :class="{ 'text-danger': scope.row.error_msg }">
                                {{ scope.row.error_msg || '--' }}
                            </span>
                        </template>
                    </el-table-column>
                </el-table>
                <div
                    v-if="tableData.length > pageSize"
                    class="pagination">
                    <el-pagination
                        background
                        layout="prev, pager, next"
                        :page-size="pageSize"
                        :total="tableData.length"
                        :current-page="currentPage"
                        @current-change="handlePageChange" />
                </div>
            </div>
        </div>

        <el-empty
            v-else-if="!loading"
            description="暂无可显示的试算数据">
            <el-button type="primary" @click="backToUpload">返回上传</el-button>
        </el-empty>
    </div>
</template>

<script>
import qs from 'qs'

const REQUEST_TYPE = 1

export default {
    name: 'TaxBatchResult',
    data () {
        return {
            loading: false,
            summary: null,
            tableData: [],
            currentPage: 1,
            pageSize: 10,
            downloadKey: '',
            currentFullpath: ''
        }
    },
    computed: {
        pagedTableData () {
            const start = (this.currentPage - 1) * this.pageSize
            return this.tableData.slice(start, start + this.pageSize)
        }
    },
    created () {
        const fullpath = this.$route.query.fullpath || this.$route.query.path
        if (!fullpath) {
            this.$message.warning('未获取到试算文件，请重新上传')
            this.backToUpload()
            return
        }
        this.currentFullpath = fullpath
        this.downloadKey = this.$route.query.key || ''
        this.fetchResult(fullpath)
    },
    methods: {
        async fetchResult (fullpath) {
            this.loading = true
            try {
                const res = await this.$http.post(
                    '/api/user/project/trialcalculationresultsimport',
                    qs.stringify({
                        path: fullpath,
                        type: this.$route.query.type || REQUEST_TYPE
                    })
                )
                if (res.code === 0 && res.data) {
                    const successRows = (Array.isArray(res.data.listData) ? res.data.listData : []).map(item => ({
                        ...item,
                        error_msg: ''
                    }))
                    const errorRows = (Array.isArray(res.data.errorData) ? res.data.errorData : []).map(item => ({
                        ...item,
                        error_msg: item.error_msg || '未知错误'
                    }))
                    this.tableData = [...successRows, ...errorRows]
                    this.summary = {
                        total: res.data.total || 0,
                        total_money: res.data.total_money || '0.00',
                        success: res.data.success || 0,
                        success_money: res.data.success_money || '0.00',
                        fail: res.data.fail || 0,
                        fail_money: res.data.fail_money || '0.00',
                        success_tax: res.data.success_tax || '0.00',
                        to_pay: res.data.to_pay || '0.00'
                    }
                    this.downloadKey = this.downloadKey || res.data.key || res.data.download_key || res.data.file_key || ''
                    this.currentPage = 1
                    if (!this.tableData.length) {
                        this.$message.warning('未获得有效的试算数据')
                    }
                } else {
                    this.$message.error(res.message || '获取试算结果失败')
                }
            } catch (err) {
                this.$message.error('获取试算结果失败，请稍后再试')
            } finally {
                this.loading = false
            }
        },
        backToUpload () {
            this.$router.replace('/index/tax/batch')
        },
        handleExport () {
            if (!this.downloadKey) {
                this.$message.warning('暂无可导出的试算结果')
                return
            }
            const token = localStorage.getItem('token') || ''
            if (!token) {
                this.$message.error('缺少登录信息，请重新登录后再试')
                return
            }
            const base = process.env.VUE_APP_BASE || ''
            const exportUrl = `${base}/api/user/downloadresultsimport?key=${encodeURIComponent(this.downloadKey)}&authorization=${encodeURIComponent(token)}`
            window.open(exportUrl, '_blank')
        },
        handlePageChange (page) {
            this.currentPage = page
        },
        rowClassName ({ row }) {
            return row.error_msg ? 'row-error' : ''
        },
        indexMethod (index) {
            return (this.currentPage - 1) * this.pageSize + index + 1
        },
        formatAmount (value) {
            if (value === null || value === undefined || value === '') {
                return '0.00'
            }
            const num = Number(value)
            if (Number.isNaN(num)) {
                return value
            }
            return num.toFixed(2)
        }
    }
}
</script>

<style scoped lang="scss">
@import "../../assets/style/var";
@import "../../assets/style/sub.scss";

.tax-batch-result {
    .content-box {
        background: #fff;
        padding: 24px;
        border-radius: 4px;
        box-shadow: 0 2px 6px rgba(31, 49, 108, 0.05);
    }

    .settlement {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: 0 24px;
        font-size: 16px;
        padding-bottom: 12px;
        border-bottom: 1px solid $--border-color-extra-light;

        .money span {
            margin-right: 8px;
        }

        .actions {
            display: flex;
            align-items: center;

            .el-button + .el-button {
                margin-left: 8px;
            }
        }
    }

    .data-card__box {
        display: flex;
        padding: 20px 24px 16px;

        .data-card {
            flex: 1;
            display: flex;
            align-items: center;
            padding: 0 18px;
            border-right: 1px solid $--border-color-extra-light;

            &:last-child {
                border-right: none;
            }

            i {
                font-size: 12px;
                color: #4b74ff;
            }

            .content {
                flex: 1;
                margin-left: 8px;

                .bd {
                    display: inline-block;
                    font-size: 12px;
                    margin-bottom: 8px;
                    color: #909399;
                }

                .hd {
                    font-size: 18px;
                    color: #303133;
                }
            }

            .icon .svg-icon {
                font-size: 42px;
            }
        }
    }

    .table {
        margin-top: 12px;

        .pagination {
            display: flex;
            justify-content: center;
            padding: 16px 0;
        }
    }

    .text-danger {
        color: #f56c6c;
    }

    ::v-deep .row-error td {
        background-color: #fff5f5;
    }
}
</style>
